import React, { ForwardedRef, forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useIntlFormat } from 'utils/intl/useIntlFormat';
import styled from 'styled-components';
import { Button, Dialog } from '@beeline.com/bp-design-system';

interface Props {
  title?: string;
  confirmLabel?: string;
}

export interface ConfirmDialogFormProps<DATA> {
  closeDialog: () => void;
  data: DATA | undefined;
  setData: (data: DATA | undefined) => void;
}

export interface ConfirmDialogOptions<DATA = void> {
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  dataTest?: string;
  /**
   * Depending on the mode, the dialog will have a different look and feel.
   * Warning mode will have a red button confirming the action and be located on the left side of the dialog,
   * while info mode will have a blue button confirming the action and be located on the right side of the dialog.
   * @default 'warning'
   */
  mode?: 'warning' | 'info';
  confirmButton?: {
    red?: boolean;
    text?: string;
    dataTest?: string;
  };
  cancelButton?: {
    text?: string;
    dataTest?: string;
  };
  initialFormData?: DATA | undefined;
  form?: (props: ConfirmDialogFormProps<DATA>) => React.ReactNode;
}

export interface ConfirmDialogAPI<DATA = void> {
  open(options?: ConfirmDialogOptions<DATA>): Promise<DATA>;
  close(): void;
}

export const ConfirmDialog = forwardRef<ConfirmDialogAPI, Props>(
  ({ title, confirmLabel }, ref: ForwardedRef<ConfirmDialogAPI>) => {
    const [open, setIsOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmDialogOptions | undefined>(undefined);
    const format = useIntlFormat();
    const onPromiseRefs = useRef<[resolve: Function, reject: Function] | undefined>(undefined);
    const [data, setData] = useState<any>();

    const close = useCallback(
      (resolve?: boolean) => {
        onPromiseRefs.current?.[resolve ? 0 : 1]?.(resolve ? data : undefined);
        onPromiseRefs.current = undefined;
        setIsOpen(false);
      },
      [data]
    );

    useImperativeHandle(
      ref,
      () =>
        ({
          open: (dialogOptions) => {
            const promise = new Promise((resolve, reject) => {
              onPromiseRefs.current = [resolve, reject];
            });
            setIsOpen(true);
            setOptions(dialogOptions);
            setData(dialogOptions?.initialFormData);
            return promise;
          },
          close
        }) as ConfirmDialogAPI
    );

    const warningMode = !options?.mode || options?.mode === 'warning';
    const infoMode = !warningMode;

    const actions = [
      <Button
        key="confirm-button"
        color={options?.confirmButton?.red || warningMode ? 'secondary' : 'primary'}
        variant={infoMode ? 'contained' : undefined}
        dataTest={options?.confirmButton?.dataTest ?? 'dialog-confirm-button'}
        onClick={() => {
          close(true);
        }}
      >
        {options?.confirmButton?.text ?? confirmLabel ?? format('general.Confirm')}
      </Button>,
      <Button
        key="cancel-button"
        variant={warningMode ? 'contained' : undefined}
        color="primary"
        onClick={() => close()}
        dataTest={options?.cancelButton?.dataTest ?? 'dialog-cancel-button'}
      >
        {options?.cancelButton?.text ?? format('general.goBack')}
      </Button>
    ];

    return (
      <Dialog
        open={open}
        onClose={() => close()}
        title={(options?.title as string) ?? title ?? format('general.confirmDeleteTitle')}
        subTitle={options?.subTitle ? options?.subTitle : format('general.confirmDeleteContent')}
        dataTest={options?.dataTest ?? 'confirm__dialog'}
        width="xs"
        disableEscapeKeyDown
        actions={options?.mode === 'info' ? actions.reverse() : actions}
      >
        {options?.form ? (
          <StyledFormContent>{options.form({ closeDialog: () => close(), data, setData })}</StyledFormContent>
        ) : null}
      </Dialog>
    );
  }
);

const StyledFormContent = styled.div`
  margin-top: 8px;
`;
